/* eslint-disable @typescript-eslint/no-explicit-any */

import { CalculatorInputs, TermLength, AllocatedStrategy, InvestmentIndex } from "../model/CalculatorInputs";
import { pipe } from "fp-ts/lib/pipeable";
import { fold, Option } from "fp-ts/lib/Option";
import { currencyFormatter } from "./commonFunctions";
import { CalculatorConfig } from "../model/CalculatorConfig";


function dispatchAnalyticsEvent(evt: any) {
  try {
    (window as any).dispatchEvent(evt);
    const windowLocation = (window as any).location;
    if(windowLocation.host.includes('prudential.com')){
      let originName = windowLocation.origin;
      if(windowLocation.ancestorOrigins && windowLocation.ancestorOrigins.length > 0){
        originName = windowLocation.ancestorOrigins[0];
      }
      (window as any).parent.postMessage({'name':'flexguard', 'type':evt.type, 'detail':evt.detail}, originName);
    }
  } catch (error) {
    console.log("Analytics error: ", evt);
  }
}

export const ANALYTICS_SCREEN_PREMIUM = "Premium";
export const ANALYTICS_SCREEN_STRATEGY = "Index Crediting Strategy";
export const ANALYTICS_SCREEN_BUFFER = "Protection (buffer)";
export const ANALYTICS_SCREEN_INDEX = "Index";

export const ANALYTICS_SCREENS = [ANALYTICS_SCREEN_PREMIUM, ANALYTICS_SCREEN_STRATEGY, ANALYTICS_SCREEN_BUFFER, ANALYTICS_SCREEN_INDEX];

export const analyticsToolStartEvent = () => {
  const evt = new CustomEvent("FlexGuardToolStart", {
    detail: {
      ScreenName: ANALYTICS_SCREEN_PREMIUM
    }
  });
  dispatchAnalyticsEvent(evt)
};



export const analyticsScreenChangeEvent = (screenName: string) => {
  const screens = [ANALYTICS_SCREEN_STRATEGY, ANALYTICS_SCREEN_BUFFER, ANALYTICS_SCREEN_INDEX];
  if (screens.includes(screenName)) {
    const evt = new CustomEvent("FlexGuardNewScreen", {
      detail: {
        ScreenName: screenName
      }
    });
    dispatchAnalyticsEvent(evt);
  }

};

export const analyticsScreenChangeEventWithInputs = (screenName: string, inputs: CalculatorInputs) => {
  const screens = [ANALYTICS_SCREEN_STRATEGY, ANALYTICS_SCREEN_BUFFER, ANALYTICS_SCREEN_INDEX];
  if (screens.includes(screenName)) {
    const Term: string | undefined = pipe(inputs.term, fold(() => undefined, (t) => t.years.toString()));
    const PremiumAmount: string | undefined = pipe(inputs.premiumPayment, fold(() => undefined, (p) => currencyFormatter(p)));

    const evt = new CustomEvent("FlexGuardNewScreen", {
      detail: {
        ScreenName: screenName,
        ToolInput: {
          PremiumAmount,
          Term
        }
      }
    });
    dispatchAnalyticsEvent(evt);
  }

};

function getDefaultDateRange(investmentIndex: Option<InvestmentIndex>, term: TermLength, config: CalculatorConfig): string | undefined {
  const dates = config.availableDateRanges;
  return pipe(investmentIndex,
    fold(() => undefined,
      (index) => {
        const range = dates.getRangeForInvestmentIndex(index);
        if (range) {
          const end = range.endYear;
          const start = (end - (term.years - 1))
          return `${start} - ${end}`
        } else {
          return undefined;
        }
      }));
}

function getCustomDateRange(startYear: number, term: TermLength): string {
  const endYear = startYear + (term.years - 1);
  return `${startYear} - ${endYear}`;
}

function getStrategyAnalytics(idx: number, allocatedStrategy: AllocatedStrategy, term: TermLength, config: CalculatorConfig) {
  const strategy = allocatedStrategy.strategy;
  const StrategyType: string | undefined = pipe(strategy.strategyType, fold(() => undefined, (st) => st.label));
  const StrategyBuffer: string | undefined = pipe(strategy.buffer, fold(() => undefined, (buf) => `${buf.percentage}%`));
  const StrategyIndex: string | undefined = pipe(strategy.investmentIndex, fold(() => undefined, (invIdx) => invIdx.label))
  const StrategyAllocation: string | undefined = pipe(allocatedStrategy.allocation, fold(() => undefined, (percentage) => `${percentage}%`))

  const customRange: string | undefined = pipe(
    allocatedStrategy.customStartYear,
    fold(() => getDefaultDateRange(strategy.investmentIndex, term, config),
      (startYear) => getCustomDateRange(startYear, term)));


  return {
    StrategyNumber: `Strategy${idx + 1}`,
    StrategyType,
    StrategyBuffer,
    StrategyIndex,
    StrategyAllocation,
    customRange
  };
}

interface StrategyAnalytics {
  StrategyNumber: string | undefined;
  StrategyType: string | undefined;
  StrategyBuffer: string | undefined;
  StrategyIndex: string | undefined;
  StrategyAllocation: string | undefined;
  customRange?: string | undefined;
  ViewType?: string | undefined;
}


export const analyticsToolCompleteEvent = (inputs: CalculatorInputs, config: CalculatorConfig) => {
  try {
    const Term: string | undefined = pipe(inputs.term, fold(() => undefined, (t) => t.years.toString()));
    const PremiumAmount: string | undefined = pipe(inputs.premiumPayment, fold(() => undefined, (p) => currencyFormatter(p)));
    const Strategies: Array<StrategyAnalytics> = [];
    pipe(inputs.term,
      fold(() => { return; },
        (termLength) => {
          for (let i = 0; i < inputs.strategyList.getStrategyCount(); i++) {
            Strategies.push(getStrategyAnalytics(i, inputs.strategyList.getAllocatedStrategy(i), termLength, config));
          }
        }));

    const evt = new CustomEvent("FlexGuardToolComplete", {
      detail: {
        ScreenName: "FlexGuardToolComplete",
        ToolInput: {
          PremiumAmount,
          Term,
          Strategies
        }
      }
    });
    dispatchAnalyticsEvent(evt);
  } catch (error) {
    console.log("Analytics error: " + error);
  }
};


export const analyticsCustomRangeEvent = (index: number, inputs: CalculatorInputs, config: CalculatorConfig) => {
  try {
    const ToolOutput: StrategyAnalytics | undefined = pipe(inputs.term,
      fold(() => undefined,
        (termLength) => getStrategyAnalytics(index, inputs.strategyList.getAllocatedStrategy(index), termLength, config)));


    const evt = new CustomEvent("FlexGuardCustomRange", {
      detail: {
        ScreenName: "FlexGuardCustomRange",
        ToolOutput
      }
    });
    dispatchAnalyticsEvent(evt);
  } catch (error) {
    console.log("Analytics error: " + error);
  }
}

export const analyticsStrategyViewEvent = (index: number, inputs: CalculatorInputs, config: CalculatorConfig) => {
  try {
    const ToolOutput: StrategyAnalytics | undefined = pipe(inputs.term,
      fold(() => undefined,
        (termLength) => getStrategyAnalytics(index, inputs.strategyList.getAllocatedStrategy(index), termLength, config)));

    const evt = new CustomEvent("FlexGuardStrategyView", {
      detail: {
        ScreenName: "FlexGuardStrategyView",
        ToolOutput
      }
    });
    dispatchAnalyticsEvent(evt);
  } catch (error) {
    console.log("Analytics error: " + error);
  }
}

export const analyticsViewChangeEvent = (index: number, viewType: string, inputs: CalculatorInputs, config: CalculatorConfig) => {
  try {
    const ToolOutput: StrategyAnalytics | undefined = pipe(inputs.term,
      fold(() => undefined,
        (termLength) => getStrategyAnalytics(index, inputs.strategyList.getAllocatedStrategy(index), termLength, config)));
    if (ToolOutput) {
      delete ToolOutput.customRange;
      ToolOutput.ViewType = viewType;
    }
    const evt = new CustomEvent("FlexGuardViewChange", {
      detail: {
        ScreenName: "FlexGuardViewChange",
        ToolOutput
      }
    });
    dispatchAnalyticsEvent(evt);
  } catch (error) {
    console.log("Analytics error: " + error);
  }
}


export const analyticsToolRestartEvent = (restartType: string) => {
  const evt = new CustomEvent("FlexGuardToolRestart", {
    detail: {
      ScreenName: ANALYTICS_SCREEN_PREMIUM,
      ButtonClicked: restartType
    }
  });
  dispatchAnalyticsEvent(evt);
}

export const analyticsErrorEvent = (errorType: string, errorString: string) => {
  const evt = new CustomEvent("FlexGuardToolError", {
    detail: {
      "TypeOfError": errorType,
      "ErrorDes": errorString
    }
  });
  dispatchAnalyticsEvent(evt);
}


export const analyticsTooltipEvent = (tooltipTitle: string) => {
  const evt = new CustomEvent("FlexGuardToolTip", {
    detail: {
      TooltipName: tooltipTitle
    }
  });
  dispatchAnalyticsEvent(evt);
}